import {
	Controller,
	useFormContext,
	useWatch,
} from "react-hook-form";
import Grid from "@mui/material/Grid2";
import { Checkbox, FormGroup, Input } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import FormField from "components/cck/FormField";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import AgreementTypeSelect from "view/agreements/components/selects/AgreementTypeSelect";
import CostTypeSelect, { CostTypeSelectOption } from "view/costtypes/components/selects/CostTypeSelect";
import CalculationMethodForm from "view/calculationmethods/CalculationMethodForm";
import type { AgreementTypeEnum, AgreementConditionType, CalculationMethodType } from "__generated__/graphql";
import { CharterSubtype, CharterType, RelationType } from "__generated__/enums";
import { Fragment, ReactNode } from "react";
import { Session } from "ezStore";
import type { AgreementType } from "types/index";
import AgreementConditionForm from "view/agreementconditions/AgreementConditionForm";
import { useSchema } from "@schemas/agreements/useSchema";
import { AgreementSchema } from "view/agreements/schema";
import type { UpdateAgreement } from "view/agreements/AgreementCreateEdit";
import { getDefaultInvoiceGroupType } from "view/agreements/utils/AgreementUtils";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";

type AgreementFormProps =  {
	index: number;
	defaultAgreement: {
		id?: number;
		uuid?: string;
		type?: AgreementTypeEnum;
	};
	updateAgreement: UpdateAgreement;
	currency: CurrencyFragment;
	session: Session;
	agreementTypes: AgreementType[];
	customAgreementTypes?: AgreementTypeEnum[];
}

type FormValues = {
	charterType: CharterType;
	charterSubtype: CharterSubtype;
	charters?: {
		id: number;
		voyages: {
			id?: number;
			ship: {
				id: number;
				name: string;
			};
		}[];
	}[];
	agreements: {
		id?: number;
		uuid?: string;
		type: AgreementTypeEnum | null;
		costType?: CostTypeSelectOption;
		invoiceGroupType?: { id?: number | null; name?: string | null } | null;
		description?: string;
		manualActionRequired?: boolean;
		calculationMethod: {
			id?: number;
			type: CalculationMethodType | null;
		} | null;
		conditions: {
			id?: number;
			type?: AgreementConditionType;
		}[];
		invoiceTo?: { id: number; name: string } | undefined;
	}[];
	isParentContract?: boolean;
	parentContract?: {
		id?: number;
	};
}
type FormField = {
	id: string;
	render: () => ReactNode;
}

const AgreementForm = ({ customAgreementTypes, index, currency, updateAgreement, agreementTypes }: AgreementFormProps) => {
	const { formState: { errors }, control } = useFormContext<FormValues>();

	const id = useWatch({ control, name: `agreements.${index}.id` });
	const type = useWatch({ control, name: `agreements.${index}.type` });
	const charterType = useWatch({ control, name: "charterType" });
	const charterSubtype = useWatch({ control, name: "charterSubtype" });
	const calculationMethodType = useWatch({ control, name: `agreements.${index}.calculationMethod.type` });
	const charters = useWatch({ control, name: "charters" }) ?? [];
	const agreements = useWatch({ control, name: "agreements" }) ?? [];
	const hasParentContract = useWatch({ control, name: "parentContract" }) ?? false;

	const currentAgreement = useWatch({ control, name: `agreements.${index}` });

	const schema = useSchema(type);

	const hiddenFields = Object.entries(schema?.agreementFields ?? {}).filter(([ , x ]) => {
		const rules = x.rules;
		return rules?.hidden || rules?.hiddenIf?.({ calculationMethodType: calculationMethodType });
	}).map(([ id ]) => id);

	const getDefaultCostType = (agreementType: AgreementTypeEnum | null) => {
		return agreementTypes?.find(x => x?.type === agreementType)?.costType ?? undefined;
	};

	const setDefaultValues = (agreementType: AgreementTypeEnum | null) => {
		const schemaPerType = agreementType ? AgreementSchema[agreementType] : undefined;
		const typeDefaults = schemaPerType?.defaultValues ?? {};

		updateAgreement(index, {
			...currentAgreement,
			...typeDefaults,
			type: agreementType,
			costType: getDefaultCostType(agreementType),
			invoiceGroupType: getDefaultInvoiceGroupType(agreementTypes, agreementType, charterType),
		});
	};

	const AgreementFormFields: FormField[] = [
		{
			id: "agreement.type",
			render: () => id == null && charterType && (
				<Grid size={{ md: 4, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${index}.type`}>
						<FormGroup label={intl.formatMessage({ id: "AGREEMENT_TYPE" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.type`}
								control={control}
								rules={{ required: true }}
								render={({ field: { onChange, value } }) => (
									<AgreementTypeSelect
										agreementTypes={agreementTypes}
										charterType={charterType}
										charterSubtype={charterSubtype}
										customAgreementTypes={customAgreementTypes}
										value={value}
										onChange={(v) => {
											onChange(v);
											setDefaultValues(v);
										}}
									/>
								)}
							/>
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "agreement.costType",
			render: () => type && (
				<Grid size={{ md: 4, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${index}.costType`}>
						<FormGroup label={intl.formatMessage({ id: "COST_TYPE" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.costType`}
								control={control}
								render={({ field }) => <CostTypeSelect {...field} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "agreement.description",
			render: () => type && (
				<Grid size={{ md: 4, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${index}.description`}>
						<FormGroup label={intl.formatMessage({ id: "DESCRIPTION" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.description`}
								control={control}
								render={({ field }) => <Input {...field} value={field.value || undefined} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "agreement",
			render: () => type && schema?.Form && (
				<schema.Form
					index={index}
					hiddenFields={hiddenFields}
					contract={{ charters, agreements }}
				/>
			),
		},
		{
			id: "agreement.calculationMethod",
			render: () => type && (
				<CalculationMethodForm
					agreementTypes={agreementTypes}
					customAgreementTypes={customAgreementTypes}
					updateAgreement={updateAgreement}
					index={index}
					currency={currency} />
			),
		},
		{
			id: "agreement.invoiceTo",
			render: () => charterSubtype !== CharterSubtype.Supplier && (
				<Grid size={{ md: 4, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${index}.invoiceTo`}>
						<FormGroup label={intl.formatMessage({ id: "INVOICE_TO" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.invoiceTo`}
								control={control}
								render={({ field }) => (
									<RelationSelect
										{...field}
										types={[ RelationType.Supplier ]}
										onChange={option => field.onChange(option ? { id: option.id, name: option.name } : undefined)}
									/>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "agreement.conditions",
			render: () => type && !hasParentContract && (
				<AgreementConditionForm index={index} />
			),
		},
		{
			id: "agreement.manualActionRequired",
			render: () => type && (
				<Grid size={12}>
					<FormField errors={errors} name={`agreements.${index}.manualActionRequired`}>
						<FormGroup labelPlacement="left-center" label={intl.formatMessage({ id: "MANUAL_ACTION_REQUIRED" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.manualActionRequired`}
								control={control}
								render={({ field }) => <Checkbox {...field} checked={field.value} value={undefined} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	return (
		<Grid container spacing={2}>
			{AgreementFormFields
				.filter(x => {
					const rules = schema?.agreementFields?.[x.id]?.rules;
					return !rules?.hidden && !rules?.hiddenIf?.({ calculationMethodType: calculationMethodType });
				})
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))}
		</Grid>
	);
};

export default AgreementForm;
